import React, { useState, useMemo, useRef } from "react";
import { RCCAlert, RCCButton, RCCDropDownItem, RCCGrid, RCCHookToast, RCCIcon, RCCRefSpreadsheetProps, RCCServicesApi, RCCTooltip } from "react-common-components";
import PageTitle from "src/components/PageTitle";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import Loader from "src/components/Loader";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import { useForm } from "react-hook-form";
import LovSubsidiary from "src/components/Lov/Subsidiary";
import Spreadsheet from "src/components/Spreadsheet";
import LovBudgetPlanYears from "src/components/Lov/BudgetPlanYears";
import Tabs from "src/components/Tabs";
import ConfigsContainer from "src/components/ConfigsContainer";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import DropdownWithBackground from "src/components/DropdownWithBackground";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import Title from "src/components/Title";
import { ExtendedValues } from "src/components/HierarchyFinancialManagement/interface";
import AppIcons from "src/components/AppIcons";
import ReactDOMServer from "react-dom/server";
import { useAuth } from "src/context/Auth/context";
import useLayoutEffectSkipInitialRender from "src/hooks/useLayoutEffectSkipInitialRender";
import stringToNumber from "src/utils/stringToNumber";
import { IScreenBudgets, ITabScreenBudgets, IUrlParams } from "./interface";
import IOriginFinancialManagement, { IOftba001 } from "../../interface";
import ModalPopulateValuesPlanned from "../ModalPopulateValuesPlanned";
import { IRefModalPopulateValuesPlanned } from "../ModalPopulateValuesPlanned/interface";
import ModalExportDelimitedSctructure from "../ModalExportStructure";
import { IRefModalExportDelimitedSctructure } from "../ModalExportStructure/interface";
import { IRefModalImportStructure } from "../ModalImportStructure/interface";
import ModalImportStructure from "../ModalImportStructure";
import ModalCommentsOrLink from "../ModalCommentsOrLink";
import IModalCommentsOrLinkRef from "../ModalCommentsOrLink/interface";

const getTitle = (origin: IOriginFinancialManagement, translate: (text: string, objKeys?: object | undefined) => string) => {
	const titles = {
		[IOriginFinancialManagement.SALES_REVENUE]: translate("Receita de vendas"),
		[IOriginFinancialManagement.OPERATIONAL_EXPENSES]: translate("Despesas operacionais"),
		[IOriginFinancialManagement.OTHER_EXPENSES]: translate("Outras despesas"),
		[IOriginFinancialManagement.SALES_DEDUCTION]: translate("Dedução de vendas"),
		[IOriginFinancialManagement.VARIABLE_COSTS]: translate("Custos variáveis"),
		[IOriginFinancialManagement.VARIABLE_EXPENSES]: translate("Despesas variáveis")
	};

	return titles[origin];
};

const getBudgetTypeFromOrigin = (origin: IOriginFinancialManagement) => {
	const titles = {
		[IOriginFinancialManagement.SALES_REVENUE]: "RE",
		[IOriginFinancialManagement.OPERATIONAL_EXPENSES]: "DO",
		[IOriginFinancialManagement.OTHER_EXPENSES]: "OU",
		[IOriginFinancialManagement.SALES_DEDUCTION]: "DE",
		[IOriginFinancialManagement.VARIABLE_COSTS]: "CV",
		[IOriginFinancialManagement.VARIABLE_EXPENSES]: "DV"
	};

	return titles[origin];
};

const ScreenBudgets: React.FC<IScreenBudgets> = ({
	hierarchy1,
	hierarchy2,
	hierarchy3,
	origin,
	tabs,
	configsComponent,
	keys,
	lov1Label,
	lov2Label,
	lov3Label
}) => {
	const params = useParams<{
        budgetPlanId: string;
    }>();
	
	const history = useHistory();
	const { translate, currentLanguage } = useTranslation();
	const { administrator, superUser } = useAuth();
	const { addToast } = RCCHookToast();
	const { confirm } = useConfirmModal();
	
	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);

	const [internalTabs, setInternalTabs] = useState(tabs);
	const updateInternalTabs = (budgetParam: any) => {
		const tabsFiltered = tabs.filter((tab) => tab.show === undefined || tab.show === true || (typeof tab.show === "function" && (budgetParam === undefined || tab.show(budgetParam))));
		setInternalTabs(tabsFiltered);
		tabSelected.current = tabsFiltered[0];
	};
	const showCorporativeTabs = useRef({
		hierarchy1: false,
		hierarchy2: false
	});

	const [budgetPlanTitle] = useState(objQueryParams?.name || "");
	// const [subsidiaryTitle, setSubsidiaryTitle] = useState(translate("Total"));
	const [titlePage] = useState(`${getTitle(origin, translate)} - ${budgetPlanTitle}`);

	const refModalPopulateValuesPlanned = useRef<IRefModalPopulateValuesPlanned>(null);
	const refModalExportDelimitedSctructure = useRef<IRefModalExportDelimitedSctructure>(null);
	const refModalImportStructure = useRef<IRefModalImportStructure>(null);

	const [subsidiaryId, setSubsidiaryId] = useState<number>(0);
	const subsidiaryTitle = useRef<string>(translate("Total"));
	const buttonSubmitFormRef = useRef<HTMLButtonElement>(null);
	const {control: controlFormOptions, setValue: setValueFormOptions, handleSubmit: handleSubmitFormOptions} = useForm();
	const submitFormOptions = (data: any) => {
		setSubsidiaryId(data.subsidiary);
		loadData(data);
	};
	const hierarchy1Id = useRef<number>(0);
	const hierarchy1Title = useRef<string>(translate("Total"));
	const hierarchy2Id = useRef<number>(0);
	const hierarchy2Title = useRef<string>(translate("Total"));
	const hierarchy3Id = useRef<number | undefined>(undefined);
	const hierarchy3Title = useRef<string>(translate("Total"));
	const lastLevel = useRef<{
		subsidiary?: boolean;
		hierarchy1?: boolean;
		hierarchy2?: boolean;
		hierarchy3?: boolean;
	}>({
		subsidiary: false,
		hierarchy1: false,
		hierarchy2: false,
		hierarchy3: !hierarchy3
	});
	const subsidiaryOfApportionmentCC = useRef<number | null>(null);
	
	const [budget, setBudget] = useState<any>(undefined);
	const [budgetPlan, setBudgetPlan] = useState<IOftba001>({} as IOftba001);
	const [corporativeValueOutdated, setCorporativeValueOutdated] = useState(false);
	const [showConfigs, setShowConfigs] = useState(false);
	const {control: controlConfigs, register: registerConfigs, handleSubmit: handleSubmitConfigs} = useForm();
	const [link, setLink] = useState<string | undefined>(undefined);
	const updateLink = (value: string | undefined) => {
		if(!value || (value && value.trim().length === 0)){
			setLink(undefined);
			return;
		}
			
		setLink(!value.includes("http://") && !value.includes("https://") ? `https://${value}` : value);
	};
	const onSubmitConfigs = async (data: any) => {
		try{
			const newBudget = {
				...budget,
				...data
			};
			await RCCServicesApi.postRequest({
				method: "FinancialManagement/InsertConfig",
				body: {
					budget: JSON.stringify(newBudget),
					origin
				}
			});
			updateInternalTabs(newBudget);
			setBudget(newBudget);
			addToast({
				color: "green",
				description: translate("Configurações atualizadas com sucesso"),
				title: translate("Sucesso")
			});
			
			updateLink(data[keys.link]);

			buttonSubmitFormRef.current?.click();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao inserir configurações"), addToast);
		}
	};

	useLayoutEffectSkipInitialRender(() => {
		if(budgetPlan && budgetPlan !== null && budgetPlan.ofa001Dtinicio)
			window.setTimeout(() => buttonSubmitFormRef.current?.click(), 100);
	}, [budgetPlan]);

	const { loading: loadingBudgetPlan, errorFetching: errorFetchingBudgetPlan } = useEffectFetchData<any> (
		"FinancialManagement/GetConfig", 
		{
			budgetId: Number(params.budgetPlanId),
			origin
		},
		({ data }) => {
			updateInternalTabs(data.budget);
			setBudget(data.budget);
			setBudgetPlan(data.budgetPlan);
			setCorporativeValueOutdated(data.budgetPlan?.ofa001Vlrcorpatu === "N");
			if(!data || data.budget === null)
				return;
			updateLink(data.budget[keys.link]);
		}
	);

	const [spreadsheetData, setSpreadsheetData] = useState<string[][] | undefined>(undefined);
	const spreadsheetRef = useRef<RCCRefSpreadsheetProps>(null);
	const [loading, setLoading] = useState(false);
	const [errorLoadingData, setErrorLoadingData] = useState(false);
	const [notAllowedData, setNotAllowedData] = useState(false);
	const spreadsheetCommentsOrLinkRef = useRef<IModalCommentsOrLinkRef>(null);
	const onSaveSpreadsheet = async () => {
		isSpreadsheetEdited.current = false;
		try{
			const newValuesMonthlyData: string[][] = spreadsheetRef.current ? spreadsheetRef.current.getData() as string[][] : [];
			newValuesMonthlyData.pop();
			newValuesMonthlyData.pop();
			await RCCServicesApi.postRequest({
				method: "FinancialManagement/InsertFinancialManagementSpreadsheet",
				body: {
					budgetPlanId: params.budgetPlanId,
					origin,
					subsidiary: subsidiaryId || "",
					hierarchy1: hierarchy1Id.current !== 0 ? hierarchy1Id.current.toString() : "",
					hierarchy2: hierarchy2Id.current !== 0 ? hierarchy2Id.current.toString() : "",
					hierarchy3: hierarchy3Id.current ? hierarchy3Id.current.toString() : "",
					currentLanguage,
					spreadsheet: {
						data: newValuesMonthlyData
					},
					type: tabSelected.current.value
				}
			});

			if(origin === IOriginFinancialManagement.OPERATIONAL_EXPENSES && budget.off001Usarateiocc === "S" && tabSelected.current.value === "corporative")
				setCorporativeValueOutdated(true);

			addToast({
				color: "green",
				description: translate("Valores adicionados com sucesso"),
				title: translate("Sucesso")
			});

		}catch(err){
			standardErrorMessages(err, translate("Erro ao adicionar valores"), addToast);
		}
	};

	const updateCorporativeValues = () => {
		confirm(
			translate("Atualizar valores corporativos"), 
			`${translate("Esse processo pode demorar alguns minutos, deseja continuar")}?`,
			async () => {
				if(!params.budgetPlanId)
					return;

				await RCCServicesApi.putRequest({
					method: "FinancialManagement/UpdateCorporativeValues",
					urlParameters: {
						budgetPlanId: params.budgetPlanId
					}
				});

				setCorporativeValueOutdated(false);

				addToast({
					color: "green",
					description: translate("Valores corporativos atualizados com sucesso"),
					title: translate("Sucesso")
				});

				urlParamsLastLoaded.current = {} as IUrlParams;
				buttonSubmitFormRef.current?.click();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao atualizar valores corporativos"), addToast);
			},
			false
		);
	};

	const tabSelected = useRef<ITabScreenBudgets>(internalTabs[0]);
	const updateCorporativeTab = () => {
		let updateStateInternalTabs = [{...tabs[0], showSaveButton: true},{...tabs[1], show: (bud: any) => bud.off001Usarateiocc.toString() === "S" || bud.off001Usarateiocc === true, showSaveButton: false}];
		if(!(budget.off001Usarateiocc === true || budget.off001Usarateiocc === "S")){
			setInternalTabs(updateStateInternalTabs);
			return;
		}
		if(showCorporativeTabs.current.hierarchy1 && showCorporativeTabs.current.hierarchy2){
			updateStateInternalTabs = [
				{
					...tabs[0],
					showSaveButton: false
				},
				{
					...tabs[1],
					show: (bud: any) => bud.off001Usarateiocc.toString() === "S" || bud.off001Usarateiocc === true,
					showSaveButton: subsidiaryOfApportionmentCC.current?.toString() === subsidiaryId.toString()
				}
			];
		}
		else if(lastLevel.current.hierarchy1 && lastLevel.current.hierarchy2){
			updateStateInternalTabs[1].show = false;
			updateStateInternalTabs[0].showSaveButton = true;
		}
		setInternalTabs(updateStateInternalTabs);
		tabSelected.current = {
			...tabSelected.current,
			showSaveButton: updateStateInternalTabs.find((x) => x.value === tabSelected.current.value)?.showSaveButton ?? false
		};
	};
	const urlParamsLastLoaded = useRef<IUrlParams>({} as IUrlParams);
	const [additionalUrl, setAdditionalUrl] = useState("");
	const loadData = async (dataForm: any) => {
		if(!params.budgetPlanId)
			return;
		
		try{
			setLoading(true);
			setErrorLoadingData(false);
			setNotAllowedData(false);

			const urlParams = {
				ofa001Id: params.budgetPlanId,
				origin,
				subsidiary: dataForm.subsidiary || "",
				hierarchy1: hierarchy1Id.current !== 0 ? hierarchy1Id.current.toString() : "",
				hierarchy2: hierarchy2Id.current !== 0 ? hierarchy2Id.current.toString() : "",
				hierarchy3: hierarchy3Id.current ? hierarchy3Id.current.toString() : "",
				period: dataForm.period ?? (
					budgetPlan && budgetPlan !== null && (
						new Date(new Date(budgetPlan.ofa001Dtinicio).getFullYear(), 0, 1) > new Date() || 
						new Date(new Date(budgetPlan.ofa001Dtfim).getFullYear(), 11, 31) < new Date()
					)  ? new Date(budgetPlan.ofa001Dtinicio).getFullYear().toString() : new Date().getFullYear().toString()
				),
				currentLanguage,
				type: tabSelected.current.value
			};

			if(origin === IOriginFinancialManagement.SALES_REVENUE || origin === IOriginFinancialManagement.OPERATIONAL_EXPENSES || origin === IOriginFinancialManagement.OTHER_EXPENSES)
				setAdditionalUrl(`&year=${urlParams.period}${urlParams.subsidiary.length > 0 ? `&subsidiary=${urlParams.subsidiary}` : ""}${urlParams.hierarchy1.length > 0 ? `&lov1=${urlParams.hierarchy1}` : ""}${urlParams.hierarchy2.length > 0 ? `&lov2=${urlParams.hierarchy2}` : ""}`);

			if(urlParamsLastLoaded.current.origin === urlParams.origin &&
			   urlParamsLastLoaded.current.ofa001Id === urlParams.ofa001Id &&
			   urlParamsLastLoaded.current.subsidiary === urlParams.subsidiary &&
			   urlParamsLastLoaded.current.hierarchy1 === urlParams.hierarchy1 &&
			   urlParamsLastLoaded.current.hierarchy2 === urlParams.hierarchy2 &&
			   urlParamsLastLoaded.current.hierarchy3 === urlParams.hierarchy3 &&
			   urlParamsLastLoaded.current.period === urlParams.period &&
			   urlParamsLastLoaded.current.type === urlParams.type)
			   {
				setLoading(false);
				return;
			   }

			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/ListForSpreadsheet",
				urlParameters: {
					...urlParams,
					isLastLevel: lastLevel.current.subsidiary && lastLevel.current.hierarchy1 && lastLevel.current.hierarchy2 && lastLevel.current.hierarchy3
				}
			});

			urlParamsLastLoaded.current = urlParams;

			if(data && data.length > 0){
				data.push([translate("Total"), "=ROUND(SUM(B1:B12), 2)", "=ROUND(SUM(C1:C12), 2)", "=ROUND(SUM(D1:D12), 2)", "=ROUND(SUM(E1:E12), 2)", "=(SUM(C1:C12) / SUM(B1:B12)) * 100"]);
				data.push([translate("Média"), "=ROUND(AVERAGE(B1:B12), 2)", "=ROUND(AVERAGE(C1:C12), 2)", "=ROUND(AVERAGE(D1:D12), 2)", "=ROUND(AVERAGE(E1:E12), 2)", "=(AVERAGE (C1:C12) / AVERAGE (B1:B12)) * 100"]);
			}
			setSpreadsheetData(data);
			setLoading(false);
		}catch(err: any){
			if(err.response && err.response.data && err.response.data.Message === "Você não tem permissão para a hierarquia solicitada"){
				setNotAllowedData(true);
			}else{
				setErrorLoadingData(true);
				standardErrorMessages(err, translate("Erro ao carregar os dados"), addToast);
			}
			setLoading(false);
		}
	};
	const isSpreadsheetEdited = useRef(false);
	const onChangeAnEditedSpreadsheet = (callback: Function) => {
		isSpreadsheetEdited.current = false;
		confirm(
			translate("Salvar alterações"), 
			`${translate("Você deseja salvar as alterações feitas")}?`,
			async () => {
				await onSaveSpreadsheet();
				callback();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao adicionar valores"), addToast);
			},
			false,
			() => {
				callback();
			}
		);
	};
	const Table = useMemo(() => {
		if(loading)
			return <Loader />;
		if(notAllowedData)
			return  <RCCAlert 
				title={translate("Atenção")}
				description={translate("Você não tem permissão para a hierarquia solicitada")}
			/>;
		if(errorLoadingData)
			return  <RCCAlert 
				title={translate("Atenção")}
				description={translate("Erro ao tentar encontrar resultados para esta pesquisa")}
			/>;
		if(!spreadsheetData)
			return  <RCCAlert 
				title={translate("Atenção")}
				description={translate("Não foram encontrados dados")}
			/>;
		const spreadsheetDataWithCommentsAndLink = spreadsheetData.map((dt) => {
			const changeLine = [...dt];
			if(typeof dt[6] === "string" && dt[6] !== "")
				changeLine[6] = ReactDOMServer.renderToStaticMarkup(
					<div className="data-financial-management-jexcel-comment" data-id={dt[0]}>
						<RCCIcon 
							name={dt[6].toLowerCase() === "false" ? "comment" : "commentFilled"} 
							color="var(--input-font-color)" 
							size={16} 
						/>
					</div>
				); 
			if(typeof dt[7] === "string" && dt[7] !== "")
				changeLine[7] = ReactDOMServer.renderToStaticMarkup(
					<div className="data-financial-management-jexcel-external-link" data-id={dt[0]}>
						<RCCIcon 
							name="linkExternal" 
							color={dt[7].toLowerCase() === "false" ? "var(--input-font-color)" : "var(--blue)"} 
							size={16} 
						/>
					</div>
				); 

			return changeLine;
		});
		const hasPermissionToEdit = (tabSelected.current.showSaveButton === true || (typeof tabSelected.current.showSaveButton === "function" && tabSelected.current.showSaveButton(budget))) && 
		lastLevel.current.subsidiary && lastLevel.current.hierarchy1 && lastLevel.current.hierarchy2 && lastLevel.current.hierarchy3;

		const plannedDate = new Date(budgetPlan.ofa001Dtplanejado);
		plannedDate.setDate(1);
		plannedDate.setHours(0, 0, 0, 0);
		const accomplishedDate = new Date(budgetPlan.ofa001Dtrealizado);
		accomplishedDate.setDate(1);
		accomplishedDate.setHours(0, 0, 0, 0);
		const date = new Date(Number(urlParamsLastLoaded.current.period), 0, 1, 0, 0, 0, 0);

		return <Spreadsheet 
			ref={spreadsheetRef}
			data={spreadsheetDataWithCommentsAndLink}
			editable={hasPermissionToEdit}
			columns={[
				{ type: "text", title: translate("Mês"), width: 30, readOnly: true, wordWrap: true },
				{ type: "numeric", title: translate("Planejado"), width: 130,  decimal: ",", readOnly: tabSelected.current.readOnlyColumn },
				{ type: "numeric", title: translate("Realizado"), width: 130, decimal: ",", readOnly: tabSelected.current.readOnlyColumn2 },
				{ type: "numeric", title: translate("Ano anterior"), width: 130, decimal: ",", readOnly: true},
				{ type: "numeric", title: `${translate("Variação")} $`, width: 130, decimal: ",", readOnly: true},
				{ type: "numeric", title: `${translate("Variação")} %`, width: 130, decimal: ",", readOnly: true},
				{ type: "html", title: " ", width: 30, readOnly: true},
				{ type: "html", title: " ", width: 30, readOnly: true}
			]}
			readonlyLastRows={2}
			formatStyleCell={(cell: any, col: any, row: any) => {
				const cellule = cell;

				if(hasPermissionToEdit && (col === 1 || col === 2)){
					date.setMonth(row);
					if((col === 1 && date < plannedDate) || (col === 2 && date < accomplishedDate)){
						cellule.parentNode.style.backgroundColor = "#f3f3f3";
						cellule.parentNode.style.color = "var(--jexcel-color-readonly)";
						cellule.parentNode.style.cursor = "no-drop";
						cellule.classList.add("readonly");
					}
				}

				let cellContent = stringToNumber(cellule.innerText, currentLanguage);
				
				if(Number.isNaN(cellContent))
					cellContent = 0;

				if(col !== 4 && col !== 5)
					return;

				// COLUNA 5 SEGUE A COR DA 4
				if(col === 5){
					const prevCell = cell.parentElement.querySelector("[data-x=\"4\"]");
					cellule.style.setProperty("color", prevCell.style.color, "important");
					return;
				}

				// COR DA COLUNA 4, PARA RECEITA DE VENDAS E DE UM JEITO DIFERENTE DAS DESPESAS
				if(origin.toString() === IOriginFinancialManagement.SALES_REVENUE){
					if(cellContent >= 0){
						cellule.style.setProperty("color", "var(--green)", "important");
						return;
					}
					if(cellContent < 0){
						cellule.style.setProperty("color", "var(--red)", "important");
						return;
					}
					return;
				}
				if(cellContent < 0){
					cellule.style.setProperty("color", "var(--green)", "important");
					return;
				}
				if(cellContent >= 0){
					cellule.style.setProperty("color", "var(--red)", "important");
				}
			}}
			format={{
				columns: [1, 2, 3, 4, 5]
			}}
			onEditionEnd={() => {
				isSpreadsheetEdited.current = true;
			}}
			showButtons={hasPermissionToEdit}
			onMount={() => {
				if (!spreadsheetRef || !spreadsheetRef.current)
					return;

				const comments = spreadsheetRef.current.HTMLElement?.querySelectorAll("div.data-financial-management-jexcel-comment");
				comments?.forEach((comment: any) => {
					const lineDate = comment.getAttribute("data-id");
					comment.addEventListener("click", () => {
						spreadsheetCommentsOrLinkRef.current?.openModalComments({
							ofa001Id: Number(params.budgetPlanId),
							date: `01/${lineDate}`, 
							hierarchy1: hierarchy1Id.current !== 0 ? hierarchy1Id.current.toString() : "",
							hierarchy2: hierarchy2Id.current !== 0 ? hierarchy2Id.current.toString() : "",
							hierarchy3: hierarchy3Id.current ? hierarchy3Id.current.toString() : "",
							origin,
							subsidiary: subsidiaryId ?? "",
							type: tabSelected.current.value
						});
					});
				});

				const externalLinks = spreadsheetRef.current?.HTMLElement?.querySelectorAll("div.data-financial-management-jexcel-external-link");
				externalLinks?.forEach((externalLink: any) => {
					const lineDate = externalLink.getAttribute("data-id");
					externalLink.addEventListener("click", () => {
						spreadsheetCommentsOrLinkRef.current?.openModalLink({
							ofa001Id: Number(params.budgetPlanId),
							date: `01/${lineDate}`, 
							hierarchy1: hierarchy1Id.current !== 0 ? hierarchy1Id.current.toString() : "",
							hierarchy2: hierarchy2Id.current !== 0 ? hierarchy2Id.current.toString() : "",
							hierarchy3: hierarchy3Id.current ? hierarchy3Id.current.toString() : "",
							origin,
							subsidiary: subsidiaryId ?? "",
							type: tabSelected.current.value
						});
					});
				});
			  }}
			actionSaveButton={onSaveSpreadsheet}
		/>;
	}, [loading, errorLoadingData, notAllowedData, spreadsheetData, budget]);
	const hierarchy1Click = (id: number, title: string, lastLvl?: boolean, extendedValues?: ExtendedValues) => {
		hierarchy1Id.current = id;
		hierarchy1Title.current = title;
		lastLevel.current = {
			...lastLevel.current,
			hierarchy1: lastLvl
		};
		if(origin === IOriginFinancialManagement.OPERATIONAL_EXPENSES){
			subsidiaryOfApportionmentCC.current = extendedValues?.subsidiaryOfApportionmentCC ?? null;
			showCorporativeTabs.current = {
				...showCorporativeTabs.current,
				hierarchy1: budget.off001Usarateiocc && extendedValues?.showCorporativeTabsOnOperationalExpense
			};
			updateCorporativeTab();
		}

		buttonSubmitFormRef.current?.click();
	};
	const hierarchy2Click = (id: number, title: string, lastLvl?: boolean, extendedValues?: ExtendedValues) => {
		hierarchy2Id.current = id;
		hierarchy2Title.current = title;
		lastLevel.current = {
			...lastLevel.current,
			hierarchy2: lastLvl
		};
		if(origin === IOriginFinancialManagement.OPERATIONAL_EXPENSES){
			showCorporativeTabs.current = {
				...showCorporativeTabs.current,
				hierarchy2: budget.off001Usarateiocc && extendedValues?.showCorporativeTabsOnOperationalExpense
			};
			updateCorporativeTab();
		}

		buttonSubmitFormRef.current?.click();
	};
	const hierarchy3Click = (id: number, title: string, lastLvl?: boolean) => {
		hierarchy3Id.current = id;
		hierarchy3Title.current = title;
		lastLevel.current = {
			...lastLevel.current,
			hierarchy3: lastLvl
		};
		buttonSubmitFormRef.current?.click();
	};

	const TitleOfSearch = useMemo(() => {
		if(loading)
			return <Loader />;
		if(errorLoadingData || !spreadsheetData)
			return  null;
		if(!spreadsheetData)
			return  <RCCAlert 
				title={translate("Atenção")}
				description={translate("Não foram encontrados dados")}
			/>;
		return <RCCGrid container item>
			<Title color="purple">
				{subsidiaryTitle.current}, {hierarchy1Title.current}, {hierarchy2Title.current}{hierarchy3Id.current ? `, ${hierarchy3Title.current}` : ""}:
			</Title>
		</RCCGrid>;
	}, [loading, errorLoadingData, spreadsheetData]);

	if(loadingBudgetPlan)
		return <Loader />;

	if(!budget || budget === null || !budgetPlan || budgetPlan === null || errorFetchingBudgetPlan)
		return <RCCAlert 
			title={translate("Atenção")}
			description={translate("Não foi possível encontrar o plano orçamentário, volte para a tela de planos orçamentários e selecione novamente")}
		/>;

	return (
		<>

			{/* Header -> Results + Buttons */}
			<PageTitle
				title={titlePage}
				icon={() => <AppIcons name="financialManagement" />}
			>
				{origin === IOriginFinancialManagement.OPERATIONAL_EXPENSES && budget.off001Usarateiocc === "S" && corporativeValueOutdated && (
					<RCCTooltip
						text={translate("Valores corporativos desatualizados")}
						color="yellow"
					>
						<RCCButton
							icon={{name: "exclamationTriangle", color: "#FFF"}}
							color="yellow"
							layout="isRounded"
							size="md"
							onClick={updateCorporativeValues}
						/>
					</RCCTooltip>
				)}
				{(administrator || superUser || budgetPlan.permission.toString() === "1" || budgetPlan.permission.toString() === "3" || budgetPlan.permission.toString() === "4") && (
					<DropdownWithBackground
						position="bottomEnd"
						callerComponent={
							<RCCButton 
								layout="isRounded"
								size="md"
								color="grey"
								icon={{
									name: "list"
								}}
							/>
						}
					>
						<RCCDropDownItem
							selfEnclose
							onClick={() => history.push(`/financialmanagement/incomestatement/${params.budgetPlanId}?name=${budgetPlanTitle}`)}
						>
							{translate("Demonstrativo resultado do exercício")}
						</RCCDropDownItem>
						<RCCDropDownItem
							selfEnclose
							onClick={() => history.push(`/financialmanagement/performance/${params.budgetPlanId}?name=${budgetPlanTitle}`)}
						>
							{translate("Performance")}
						</RCCDropDownItem>
					</DropdownWithBackground>
				)}
				{(origin === IOriginFinancialManagement.SALES_REVENUE || origin === IOriginFinancialManagement.OPERATIONAL_EXPENSES || origin === IOriginFinancialManagement.OTHER_EXPENSES) && 
					 (administrator || superUser || budgetPlan.permission.toString() === "1" || budgetPlan.permission.toString() === "2" || budgetPlan.permission.toString() === "3" || budgetPlan.permission.toString() === "4") && (
					<RCCTooltip
						text={translate("Consulta valores de orçamento")}
						color="grey"
					>
						<Link to={`/financialmanagement/consultvalues/${budgetPlan.ofa001Id}?name=${budgetPlanTitle}&budgetType=${getBudgetTypeFromOrigin(origin)}&year=2022${additionalUrl}`}>
							<RCCButton 
								layout="isRounded"
								size="md"
								icon={{
									name:"table",
									color:"#FFF",
									size:16
								}}
							/>
						</Link>
					</RCCTooltip>
				)}	
				{(administrator || superUser || budgetPlan.permission.toString() === "1") && (
					<RCCTooltip
						text={translate("Configurações")}
						color={showConfigs ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "settings", color: "#FFF", size: 16}}
							color={showConfigs ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowConfigs((prevState) => !prevState)}
						/>
					</RCCTooltip>
				)}			
				<DropdownWithBackground
					position="bottomEnd"
					callerComponent={<RCCButton 
						icon={{
							name: "dotsVertical", color: "#FFF", size: 18
						}}
						color="grey"
						layout="isRounded"
						size="md"
					/>}
				>
					<RCCDropDownItem 
						selfEnclose
						onClick={() => {
							if(!refModalPopulateValuesPlanned.current || !params.budgetPlanId)
								return;

							refModalPopulateValuesPlanned.current.openModalPopulateValuesPlanned({
								lov1: {
									value: hierarchy1Id.current !== 0 ? hierarchy1Id.current.toString() : "",
									label: `${lov1Label}: ${hierarchy1Title.current}`
								},
								lov2: {
									value: hierarchy2Id.current !== 0 ? hierarchy2Id.current.toString() : "",
									label: `${lov2Label}: ${hierarchy2Title.current}`
								},
								lov3: hierarchy3Id.current && lov3Label ? {
									value: hierarchy3Id.current !== 0 ? hierarchy3Id.current.toString() : "",
									label: `${lov3Label}: ${hierarchy3Title.current}`
								}: undefined,
								ofa001Id: params.budgetPlanId,
								period: urlParamsLastLoaded.current.period,
								subsidiary: {
									label: `${translate("Unidade/Filial")}: ${subsidiaryTitle.current}`,
									value: subsidiaryId ? subsidiaryId.toString() : ""
								},
								subtitle: titlePage,
								startMonth: budgetPlan.ofa001Mesinicio
							}, () => {
								if(origin === IOriginFinancialManagement.OPERATIONAL_EXPENSES && budget.off001Usarateiocc === "S")
									setCorporativeValueOutdated(true);
							});
						}}
					>
						<RCCIcon name="settings" />
						{translate("Popular valores planejados")}
					</RCCDropDownItem>
					{origin === IOriginFinancialManagement.OPERATIONAL_EXPENSES && corporativeValueOutdated && (
						<RCCDropDownItem 
							selfEnclose
							onClick={updateCorporativeValues}
						>
							<RCCIcon name="refresh" />
							{translate("Atualizar valores corporativos")}
						</RCCDropDownItem>
					)}
					<RCCDropDownItem 
						selfEnclose
						onClick={() => {
							if(!refModalExportDelimitedSctructure.current || !params.budgetPlanId)
								return;

							refModalExportDelimitedSctructure.current.openModalExportDelimitedStructure({
								ofa001Id: params.budgetPlanId,
								budgetId: budget[keys.id],
								period: urlParamsLastLoaded.current.period,
								subsidiary: {
									label: `${translate("Unidade/Filial")}: ${subsidiaryTitle.current}`,
									value: subsidiaryId ? subsidiaryId.toString() : ""
								},
								subtitle: titlePage,
								startMonth: budgetPlan.ofa001Mesinicio
							});
						}}
					>
						<RCCIcon name="download" />
						{translate("Exportar estrutura delimitada")}
					</RCCDropDownItem>
					<RCCDropDownItem 
						selfEnclose
						onClick={() => {
							if(!refModalExportDelimitedSctructure.current || !params.budgetPlanId)
								return;

							refModalExportDelimitedSctructure.current.openModalExportStructure({
								ofa001Id: params.budgetPlanId,
								budgetId: budget[keys.id],
								period: urlParamsLastLoaded.current.period,
								subsidiary: {
									label: `${translate("Unidade/Filial")}: ${subsidiaryTitle.current}`,
									value: subsidiaryId ? subsidiaryId.toString() : ""
								},
								subtitle: titlePage,
								startMonth: budgetPlan.ofa001Mesinicio
							});
						}}
					>
						<RCCIcon name="download" />
						{translate("Exportar estrutura do orçamento")}
					</RCCDropDownItem>
					<RCCDropDownItem 
						selfEnclose
						onClick={() => {
							if(!refModalImportStructure.current || !params.budgetPlanId)
								return;

							refModalImportStructure.current.openModalImportStructure({
								ofa001Id: params.budgetPlanId,
								budgetId: budget[keys.id],
								period: urlParamsLastLoaded.current.period,
								subtitle: titlePage,
								startMonth: budgetPlan.ofa001Mesinicio
							});
						}}
					>
						<RCCIcon name="upload" />
						{translate("Importar valores")}
					</RCCDropDownItem>
				</DropdownWithBackground>
				{link &&  (
					<RCCTooltip
						text="Link"
						color="purple"
						position="leftEnd"
					>
						<RCCButton
							layout="isRounded"
							size="md"
							icon={{
								name: "linkExternal"
							}}
							color="purple"
							onClick={() => window.open(link, "_blank")}
						/>
					</RCCTooltip>
				)}
				<RCCTooltip
					text={translate("Voltar")}
					color="grey"
				>
					<RCCButton
						icon={{name: "arrowBack", color: "#FFF"}}
						color="grey"
						layout="isRounded"
						size="md"
						onClick={() => history.push(`/financialmanagement/panel/${params.budgetPlanId}`)}
					/>
				</RCCTooltip>
			</PageTitle>

			{(administrator || superUser || budgetPlan.permission.toString() === "1") && (
				<ConfigsContainer
					showClearConfigs={false}
					showConfigs={showConfigs}
					onSubmitConfigs={handleSubmitConfigs(onSubmitConfigs)}
				>
					{!budget || budget === null ? <RCCAlert
						title={translate("Atenção")}
						description={translate("Não foram encontrados dados")}
					/>: configsComponent({
						control: controlConfigs,
						register: registerConfigs,
						budget
					})}
				</ConfigsContainer>
			)}

			{/* Subsidiary and period */}
			<form onSubmit={handleSubmitFormOptions(submitFormOptions)} style={{marginBottom: "1rem"}}>
				<RCCGrid
					container 
					item
					spacing={1}
					alignItems="center"
				>
					<RCCGrid
						container
						item
						xs={12}
						sm={6}
					>
						<LovSubsidiary 
							control={controlFormOptions}
							name="subsidiary"
							label={translate("Unidade/Filial")}
							extraParametersOnLoad={{
								budgetPlanId: Number(params.budgetPlanId)
							}}
							onChangeCapture={(e: any) => {
								subsidiaryTitle.current = e.label;
								lastLevel.current = {
									...lastLevel.current,
									subsidiary: e.extendedProps.isLeaf && e.extendedProps.level > 1
								};
								if(origin === IOriginFinancialManagement.OPERATIONAL_EXPENSES && (budget.off001Usarateiocc === true || budget.off001Usarateiocc === "S")){
									const updateStateInternalTabs = [
										{...tabs[0], showSaveButton: false},
										{...tabs[1], show: (bud: any) => bud.off001Usarateiocc.toString() === "S" || bud.off001Usarateiocc === true, showSaveButton: subsidiaryOfApportionmentCC.current?.toString() === e.value}
									];
									setInternalTabs(updateStateInternalTabs);
									tabSelected.current = {
										...tabSelected.current,
										showSaveButton: updateStateInternalTabs.find((x) => x.value === tabSelected.current.value)?.showSaveButton ?? false
									};
								}
								buttonSubmitFormRef.current?.click();
							}}
							setValue={setValueFormOptions}
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={4}
						md={2}
						lg={2}
					>
						<LovBudgetPlanYears 
							control={controlFormOptions}
							name="period"
							label={translate("Período")}
							extraParametersOnLoad={{
								budgetPlanId: Number(params.budgetPlanId)
							}}
							onChangeCapture={() => {
								buttonSubmitFormRef.current?.click();
							}}
							setValue={setValueFormOptions}
						/>
					</RCCGrid>
				</RCCGrid>
				<button 
					aria-label="Submit Form"
					ref={buttonSubmitFormRef}
					type="submit" 
					style={{ display: "none" }}
				></button>
			</form>

			<RCCGrid
				container
				item
			>
				<RCCGrid container item md={12} lg={hierarchy3 ? 7 : 6}>
					<RCCGrid
						container
						item
						md={12}
						lg={hierarchy3 ? 4 : 6}
						alignItems="flex-start"
					>
						{hierarchy1({
							ofa001Id: params.budgetPlanId,
							ofa002Id: subsidiaryId > 0 ? subsidiaryId : undefined,
							onClickLine: (id: number, title: string, lastLvl?: boolean, extendedValues?: ExtendedValues) => {
								if(isSpreadsheetEdited.current){
									onChangeAnEditedSpreadsheet(() => {
										hierarchy1Click(id, title, lastLvl, extendedValues);
									});
									return;
								}
								hierarchy1Click(id, title, lastLvl, extendedValues);
							},
							onCallbackApportionmentCC: () => {
								if(origin === IOriginFinancialManagement.OPERATIONAL_EXPENSES && budget.off001Usarateiocc === "S")
									setCorporativeValueOutdated(true);
							}}
						)}
					</RCCGrid>
					<RCCGrid
						container
						item
						md={12}
						lg={hierarchy3 ? 4 : 6}
						alignItems="flex-start"
					>
						{hierarchy2({
							ofa001Id: params.budgetPlanId,
							ofa002Id: subsidiaryId > 0 ? subsidiaryId : undefined,
							onClickLine: (id: number, title: string, lastLvl?: boolean, extendedValues?: ExtendedValues) => {
								if(isSpreadsheetEdited.current){
									onChangeAnEditedSpreadsheet(() => {
										hierarchy2Click(id, title, lastLvl, extendedValues);
									});
									return;
								}
								hierarchy2Click(id, title, lastLvl, extendedValues);
							}
						})}
					</RCCGrid>
					{hierarchy3 && (
						<RCCGrid
							container
							item
							md={12}
							lg={4}
							alignItems="flex-start"
						>
							{hierarchy3({
								ofa001Id: params.budgetPlanId,
								ofa002Id: subsidiaryId > 0 ? subsidiaryId : undefined,
								onClickLine: (id: number, title: string, lastLvl?: boolean) => {
									if(isSpreadsheetEdited.current){
										onChangeAnEditedSpreadsheet(() => {
											hierarchy3Click(id, title, lastLvl);
										});
										return;
									}
									hierarchy3Click(id, title, lastLvl);
								}
							})}
						</RCCGrid>
					)}
				</RCCGrid>
				<RCCGrid
					container
					item
					alignItems="baseline"
					style={{ flex: 1}}
				>
					<div style={{ position: "sticky", top: "1rem", width: "100%"}}>
						{TitleOfSearch}
						<Tabs 
							name="TabsFinancialManagement"
							tabInit={0}
							options={internalTabs.filter((tab) => tab.show === undefined || tab.show === true || (typeof tab.show === "function" && (budget === undefined || tab.show(budget)))).map((tab) => ({
								label: tab.label,
								content: tabSelected.current.label === tab.label ? Table : null,
								onClick: () => {
									if(isSpreadsheetEdited.current){
										onChangeAnEditedSpreadsheet(() => {
											tabSelected.current = tab;
											buttonSubmitFormRef.current?.click();
										});
										return;
									}
									tabSelected.current = tab;
									buttonSubmitFormRef.current?.click();
								}
							}))}
							styleInline="width: 100%; >div{ padding: 8px 4px; border: none; }"
						/>
					</div>
				</RCCGrid>
			</RCCGrid>

			<ModalPopulateValuesPlanned 
				ref={refModalPopulateValuesPlanned}
				onClose={() => {
					urlParamsLastLoaded.current = {} as IUrlParams;
					buttonSubmitFormRef.current?.click();
				}}
				origin={origin}
				tabs={internalTabs.filter((tab) => !tab.hideFromModalPopulate && (tab.show === undefined || tab.show === true || (typeof tab.show === "function" && (budget === undefined || tab.show(budget)))))}
			/>

			<ModalExportDelimitedSctructure 
				ref={refModalExportDelimitedSctructure}
				origin={origin}
			/>

			<ModalImportStructure 
				ref={refModalImportStructure}
				onClose={() => {
					urlParamsLastLoaded.current = {} as IUrlParams;
					buttonSubmitFormRef.current?.click();
				}}
				origin={origin}
			/>

			<ModalCommentsOrLink 
				ref={spreadsheetCommentsOrLinkRef}
				onCallback={() => {
					urlParamsLastLoaded.current = {} as IUrlParams;
					buttonSubmitFormRef.current?.click();
				}}
			/>
		</>
	);
};

export default ScreenBudgets;
